import { memo, useCallback, useRef, useState } from 'react';
import { isEmpty, isNull } from 'lodash';
// Import Components
import { Badge, Popover, Icon, BusyLoader } from '@geneui/components';
import { Notification } from 'components/Header/types';
import NotificationHeader from './NotificationHeader';
import NotificationList from './NotificationList';
// Import Hooks
import useOnClickOutside from 'hooks/UseOnClickOutside';
import useNotifications from 'hooks/UseNotifications';
// Import SCSS
import styles from './notificationPanel.module.scss';

const NotificationPanel = () => {
    const { isLoading, notifications, unreadCount, markAsRead, markAsUnread, markAllAsRead, redirectOnClick } =
        useNotifications();
    const popoverRef = useRef(null);
    const contentRef = useRef(null);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const onClickNotification = useCallback(
        (notification: Notification) => {
            if (isNull(notification.ReadDate)) {
                markAsRead(notification);
            }
            setIsPopoverOpen(false);
            redirectOnClick(notification);
        },
        [markAsRead, redirectOnClick],
    );

    useOnClickOutside(
        [popoverRef, contentRef],
        useCallback(() => {
            if (isPopoverOpen) {
                setIsPopoverOpen(false);
            }
        }, [isPopoverOpen]),
    );

    const popoverToggleHandler = () => {
        setIsPopoverOpen((prev) => !prev);
    };

    const icon = <Icon type="bc-icon-bell-outline" className="notification-icon" onClick={popoverToggleHandler} />;

    return (
        <div ref={popoverRef}>
            <Popover
                isOpen={isPopoverOpen}
                extendTargetWidth={false}
                className={styles.popover}
                align="end"
                contentRef={contentRef}
                Header={
                    <>
                        <NotificationHeader onMarkAllRead={markAllAsRead} isVisibleButton={!isEmpty(notifications)} />
                    </>
                }
                Content={
                    <BusyLoader isBusy={isLoading && isEmpty(notifications)}>
                        <NotificationList
                            notifications={notifications}
                            onClickNotification={onClickNotification}
                            onClickMarkAsRead={markAsRead}
                            onClickMarkAsUnread={markAsUnread}
                        />
                    </BusyLoader>
                }
            >
                <span>
                    {unreadCount ? (
                        <Badge count={unreadCount} maxCount={99}>
                            {icon}
                        </Badge>
                    ) : (
                        icon
                    )}
                </span>
            </Popover>
        </div>
    );
};

export default memo(NotificationPanel);
